import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface RouteErrorBoundaryProps {
  children?: ReactNode;
  /**
   * Changing this clears a caught error, so navigating away from a broken page
   * recovers on its own — boundary state otherwise survives a re-render with
   * different children, and the user would stay stuck on the error after
   * clicking to a perfectly healthy route.
   *
   * Build it with `routeResetKey`, not from the pathname alone.
   */
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
  lastResetKey?: string;
}

/**
 * What identifies "the page you are looking at" for reset purposes.
 *
 * The pathname alone is not enough: the organization can change while the URL
 * stays byte-identical and every document under the page changes. Org pages
 * carry their org in the URL (and the main shell remounts per URL org), but the
 * header picker on the cross-org admin dashboard selects another org in place,
 * and AnnotationRouteGate selects a link's organization without navigating.
 * When this keyed on pathname alone it latched: a crash caused by
 * org A's data survived the switch to a healthy org B, and because React tears
 * down the failed subtree, org B's data was never even fetched — the header
 * showed org B's name above org A's error message.
 *
 * The org id is a stable string, deliberately not the org object: object
 * identity churn would reset on every render.
 */
export const routeResetKey = (organizationId: string | undefined, pathname: string): string =>
  `${organizationId ?? "none"}:${pathname}`;

/**
 * Bounds the blast radius of a render crash to the routed page.
 *
 * Without a boundary, React 18 unmounts the whole root on any render throw — the
 * app becomes an empty `<div id="root">`, with no sidebar, no header, and no way
 * to navigate away. That is how a single trial document missing `startDate` took
 * an organization's entire app down rather than one card.
 *
 * This is deliberately NOT a substitute for guarding the data (see
 * `timestampToDate` and friends in `@/lib/utils`): a boundary converts a blank
 * page into an error card, but the user still cannot see their trials. Guards
 * are what keep the other rows rendering. The boundary is what guarantees the
 * failure is legible and escapable when a guard is missing — and one always
 * eventually is.
 *
 * Mounted around `<Routes>`, inside the app shell, so the chrome survives.
 */
class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: RouteErrorBoundaryProps,
    state: RouteErrorBoundaryState
  ): Partial<RouteErrorBoundaryState> | null {
    if (state.lastResetKey !== props.resetKey) {
      return { error: null, lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Never swallow. A silently-caught render crash hides real bugs, which is
    // worse than the blank page this replaces.
    console.error("Route render failed:", error, errorInfo.componentStack);
  }

  private handleRetry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="mx-auto max-w-lg py-12 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
        <h2 className="mt-4 text-lg font-semibold">This page failed to load</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Something in this page&apos;s data could not be displayed. The rest of the app still
          works — you can go back, or try again.
        </p>
        {/* Showing the real message costs nothing and turns an unactionable
            "something went wrong" into a bug report someone can act on. */}
        <p className="mt-4 break-words rounded bg-muted p-3 text-left font-mono text-xs">
          {error.message}
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.history.back()}>
            Go back
          </Button>
          <Button onClick={this.handleRetry}>Try again</Button>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
